/**
 * Floodlight hover system (web, v1.71). One idea everywhere: what you point
 * at lifts off the glass and catches the light. Buttons rise on a soft glow
 * (purple on the accent fill), rows brighten on a white wash, cards lift with
 * a brighter hairline and a purple bloom, headline letters squeeze on their
 * width axis under the pointer, links draw a hairline underline, labels roll
 * to a fresh copy. No hard offset shadows and no ink blocks.
 *
 * Elements opt in with dataSet attributes (react-native-web writes data-*):
 *   data-fl-btn="ink|green|outline|paper" button      (+ data-fl-roll / data-fl-label="a|b" / data-fl-ico)
 *   data-fl-row(="static")           list row    (+ data-fl-row-num / data-fl-row-body / data-fl-row-arrow / data-fl-lit)
 *   data-fl-card                      card        (+ data-fl-card-bar)
 *   data-fl-chip                      pill/toggle (data-fl-chip="on" when selected)
 *   data-fl-link                      text link
 *   data-fl-field                     input shell (focus ring on :focus-within)
 *   data-fl-input                     a bare text input (focus ring on :focus)
 *   data-fl-head / data-fl-char       display headline letters (FLHeadline)
 *   data-fl-marquee / data-fl-mq-item scrolling band (FLMarquee)
 *
 * Hover motion runs only for fine pointers; keyboard focus gets the same lit
 * state plus a visible ring. Reduced motion keeps the colour changes and drops
 * all movement. Colours are the theme's CSS variables (theme.js).
 */
import injectCss from '../../utils/injectCss';
import { IS_WEB } from '../../utils/motion';
import { flThemeCss } from './theme';

const EASE_OUT = 'cubic-bezier(0.16,1,0.3,1)';
const EASE_WIPE = 'cubic-bezier(0.7,0,0.2,1)';
const SPRING = 'cubic-bezier(0.34,1.4,0.64,1)';

/** The purple glow under a lit accent button / card (the original accent, #7C3AED). */
const GLOW = 'rgba(124,58,237,0.38)';
const LIFT_SHADOW = '0 14px 40px rgba(0,0,0,0.45)';

export const FLOODLIGHT_CSS = `
[data-fl-btn], [data-fl-row], [data-fl-card], [data-fl-chip] { position: relative; isolation: isolate; }
[data-fl-btn], [data-fl-row], [data-fl-chip] { overflow: hidden; }
[data-fl-btn] { cursor: pointer; transition: transform 420ms ${SPRING}, box-shadow 320ms ${EASE_OUT}, border-color 220ms, background-color 220ms; }
[data-fl-btn]::before, [data-fl-row]::before { content: ''; position: absolute; inset: 0; z-index: -1; pointer-events: none; border-radius: inherit;
  opacity: 0; transition: opacity 260ms ${EASE_OUT}; }
[data-fl-btn="green"]::before { background: linear-gradient(180deg, rgba(255,255,255,0.16), rgba(255,255,255,0)); }
[data-fl-btn="ink"]::before, [data-fl-btn="outline"]::before, [data-fl-btn="paper"]::before { background: var(--fl-ink-wash); }
[data-fl-row]::before { background: var(--fl-ink-wash); }
[data-fl-roll] { position: relative; overflow: hidden; }
[data-fl-label] { display: block; transition: transform 480ms ${EASE_WIPE}, color 200ms; }
[data-fl-label="b"] { position: absolute; left: 0; right: 0; top: 0; transform: translate3d(0, 110%, 0); }
[data-fl-ico], [data-fl-ico] * { transition: transform 520ms ${SPRING}, color 200ms; }
[data-fl-btn][aria-disabled="true"] { cursor: default; }
[data-fl-btn]:focus-visible, [data-fl-row]:focus-visible, [data-fl-card]:focus-visible, [data-fl-chip]:focus-visible, [data-fl-link]:focus-visible {
  outline: 2px solid var(--fl-green-ink) !important; outline-offset: 3px; }

[data-fl-row]:not([data-fl-row="static"]) { cursor: pointer; }
[data-fl-row-num], [data-fl-row-body], [data-fl-row-arrow] { transition: transform 480ms ${EASE_OUT}, opacity 260ms, color 200ms; }
[data-fl-row-arrow] { opacity: 0; transform: translate3d(-10px, 0, 0) rotate(0deg); }

[data-fl-card] { transition: transform 460ms ${EASE_OUT}, box-shadow 460ms ${EASE_OUT}, border-color 260ms; }
[data-fl-card-bar] { transform: scaleX(0); transform-origin: left; transition: transform 620ms ${EASE_OUT}; }

[data-fl-chip] { cursor: pointer; transition: background-color 220ms, color 220ms, border-color 220ms, transform 320ms ${SPRING}; }
[data-fl-link] { cursor: pointer; background-image: linear-gradient(currentColor, currentColor); background-repeat: no-repeat;
  background-position: 100% 100%; background-size: 0% 1px; transition: background-size 420ms ${EASE_WIPE}; }
[data-fl-field] { transition: border-color 200ms, box-shadow 200ms, background-color 200ms; }
[data-fl-field]:focus-within { border-color: var(--fl-green-ink) !important; box-shadow: 0 0 0 3px var(--fl-green-wash); }
[data-fl-input] { transition: border-color 200ms, box-shadow 200ms; }
[data-fl-input]:focus { border-color: var(--fl-green-ink) !important; box-shadow: 0 0 0 3px var(--fl-green-wash); }

[data-fl-word] { display: inline-block; white-space: nowrap; }
[data-fl-char] { display: inline-block; transition: font-stretch 520ms ${EASE_OUT}, color 260ms, transform 520ms ${SPRING}; }
[data-fl-char-in] { display: inline-block; transform: translate3d(0, 105%, 0); animation: flRise 900ms ${EASE_OUT} both; }
@keyframes flRise { to { transform: none; } }

[data-fl-marquee] { overflow: hidden; }
[data-fl-mq-track] { display: flex; width: max-content; animation: flMarquee var(--fl-mq-dur, 36s) linear infinite; }
[data-fl-mq-track][data-fl-mq-dir="rev"] { animation-direction: reverse; }
[data-fl-mq-item] { transition: color 200ms, font-stretch 520ms ${EASE_OUT}; cursor: default; }
@keyframes flMarquee { from { transform: translate3d(0,0,0); } to { transform: translate3d(-50%,0,0); } }

@media (hover: hover) and (pointer: fine) {
  [data-fl-btn]:not([aria-disabled="true"]):hover::before, [data-fl-row]:hover::before { opacity: 1; }
  [data-fl-btn]:not([aria-disabled="true"]):hover { transform: translate3d(0, -2px, 0); }
  [data-fl-btn="green"]:not([aria-disabled="true"]):hover { box-shadow: 0 12px 32px ${GLOW}; }
  [data-fl-btn="ink"]:not([aria-disabled="true"]):hover, [data-fl-btn="outline"]:not([aria-disabled="true"]):hover,
  [data-fl-btn="paper"]:not([aria-disabled="true"]):hover { border-color: var(--fl-hair2) !important; box-shadow: ${LIFT_SHADOW}; }
  [data-fl-btn]:not([aria-disabled="true"]):hover [data-fl-label="a"] { transform: translate3d(0, -110%, 0); }
  [data-fl-btn]:not([aria-disabled="true"]):hover [data-fl-label="b"] { transform: none; }
  [data-fl-btn]:not([aria-disabled="true"]):hover [data-fl-ico] { transform: rotate(-45deg); }

  [data-fl-row]:hover [data-fl-row-body] { transform: translate3d(8px, 0, 0); }
  [data-fl-row]:hover [data-fl-row-num] { transform: translate3d(4px, 0, 0); color: var(--fl-green-ink) !important; }
  [data-fl-row]:hover [data-fl-row-arrow] { opacity: 1; transform: translate3d(0, 0, 0) rotate(-45deg); }
  [data-fl-row]:hover [data-fl-lit] { color: var(--fl-ink) !important; }

  [data-fl-card]:hover { transform: translate3d(0, -4px, 0); border-color: var(--fl-hair2) !important; box-shadow: ${LIFT_SHADOW}, 0 0 0 1px rgba(162,89,255,0.18), 0 0 60px rgba(124,58,237,0.12); }
  [data-fl-card]:hover [data-fl-card-bar] { transform: scaleX(1); }

  [data-fl-chip]:not([data-fl-chip="on"]):hover { background-color: var(--fl-stage) !important; border-color: var(--fl-hair2) !important; }
  [data-fl-chip]:not([data-fl-chip="on"]):hover, [data-fl-chip]:not([data-fl-chip="on"]):hover * { color: var(--fl-ink) !important; }
  [data-fl-chip]:hover { transform: translate3d(0, -1px, 0); }

  [data-fl-link]:hover { background-position: 0 100%; background-size: 100% 1px; }
  [data-fl-field]:hover, [data-fl-input]:hover { border-color: var(--fl-hair2) !important; }

  [data-fl-char]:hover { font-stretch: 70%; color: var(--fl-green-ink); transform: translate3d(0, -3px, 0); }
  [data-fl-head="paper"] [data-fl-char]:hover { color: var(--fl-green-on-ink); }
  [data-fl-head="accent"] [data-fl-char]:hover { color: var(--fl-blue-ink); }
  [data-fl-marquee]:hover [data-fl-mq-track] { animation-play-state: paused; }
  [data-fl-mq-item]:hover { color: var(--fl-green-ink) !important; font-stretch: 70%; }
  [data-fl-marquee="green"] [data-fl-mq-item]:hover { color: var(--fl-blue-ink) !important; }
}

/* Focus beats hover on fields (this block comes after the hover rules). */
[data-fl-field]:focus-within, [data-fl-input]:focus { border-color: var(--fl-green-ink) !important; }

/* Keyboard parity: focus lights the same way hover does (plus the ring above). */
[data-fl-btn]:focus-visible::before, [data-fl-row]:focus-visible::before { opacity: 1; }
[data-fl-btn="green"]:focus-visible { box-shadow: 0 12px 32px ${GLOW}; }
[data-fl-row]:focus-visible [data-fl-row-arrow] { opacity: 1; transform: rotate(-45deg); }
[data-fl-row]:focus-visible [data-fl-row-num] { color: var(--fl-green-ink) !important; }
[data-fl-card]:focus-within { border-color: var(--fl-hair2) !important; }
[data-fl-card]:focus-within [data-fl-card-bar] { transform: scaleX(1); }

/* Press feedback (every pointer). */
[data-fl-btn]:not([aria-disabled="true"]):active { transform: translate3d(0, 0, 0) scale(0.98); }
[data-fl-chip]:active { transform: scale(0.97); }

@media (prefers-reduced-motion: reduce) {
  [data-fl-btn], [data-fl-btn]::before, [data-fl-row], [data-fl-row]::before, [data-fl-card], [data-fl-card-bar], [data-fl-label], [data-fl-ico], [data-fl-ico] *,
  [data-fl-row-num], [data-fl-row-body], [data-fl-row-arrow], [data-fl-chip], [data-fl-link], [data-fl-char], [data-fl-mq-item] { transition: none !important; }
  [data-fl-btn]:hover, [data-fl-btn]:active, [data-fl-card]:hover, [data-fl-chip]:hover, [data-fl-chip]:active, [data-fl-char]:hover, [data-fl-row-num] { transform: none !important; }
  [data-fl-label="b"] { display: none; } [data-fl-label="a"] { transform: none !important; }
  [data-fl-char-in] { animation: none; transform: none; }
  [data-fl-mq-track] { animation: none; }
  [data-fl-row]:hover [data-fl-row-body], [data-fl-row]:hover [data-fl-row-arrow] { transform: none !important; }
}
@media (forced-colors: active) { [data-fl-btn]::before, [data-fl-row]::before { display: none; } }
`;

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Archivo:ital,wdth,wght@0,62..125,100..900;1,62..125,100..900&display=swap';

/** Inject the Floodlight CSS and make sure Archivo is requested (web only, once). */
export function ensureFloodlight() {
    if (!IS_WEB || typeof document === 'undefined') return;
    injectCss('floodlight-theme', flThemeCss());
    injectCss('floodlight', FLOODLIGHT_CSS);
    if (!document.querySelector('link[data-fl-font]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = FONT_HREF;
        link.setAttribute('data-fl-font', '');
        document.head.appendChild(link);
    }
}
